import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  Stack,
  TextField,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MUITypography from "../../Components/MUITypography";
import Loader from "../../Components/Loader";
import { API_RESEND_OTP, API_VERIFY_OTP } from "../../Util/URLConstants";
import { saveAuthFromResponse } from "../../Util/jwtAuth";
import axios from "axios";
import { toast } from "react-toastify";
import {
  align_center,
  align_right,
  align_row,
} from "../../Styles/VMStyles";

const OTPDialog = ({
  showOtpDialog,
  handleClose,
  arrived,
  user_id,
  handleNext,
  handleOTPVerify,
}) => {
  const [isDialogDisplay, setIsDialogDisplay] = useState(showOtpDialog);
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [resendDisabled, setResendDisabled] = useState(false);
  const [timer, setTimer] = useState(30);
  const [loading, setLoading] = useState(false);

  const inputRefs = useRef([]);

  // ✅ Auto-focus first input when dialog opens
  useEffect(() => {
    if (showOtpDialog) {
      setOtp(Array(6).fill(""));
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    }
    setIsDialogDisplay(showOtpDialog);
  }, [showOtpDialog]);

  const handleOtpChange = (index, value) => {
    if (!/^[0-9]?$/.test(value)) return;
    const updatedOtp = [...otp];
    updatedOtp[index] = value;
    setOtp(updatedOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleResendClick = async () => {
    setLoading(true);
    try {
      // ✅ Clear OTP when resend is clicked
      setOtp(Array(6).fill(""));
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);

      setResendDisabled(true);
      setTimer(30);

      await axios.post(API_RESEND_OTP, { user_id });

      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setResendDisabled(false);
            return 30;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error("Error resending OTP:", error);
      toast.error("Failed to resend OTP");
      setResendDisabled(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const otpValue = otp.join("");
    if (otpValue.length !== 6) return;

    setLoading(true);
    const reqBody = { otp: otpValue, user_id };

    try {
      const { data } = await axios.post(API_VERIFY_OTP, reqBody, {
        headers: { "Content-Type": "application/json" },
      });

      if (data.status_code === 200 && data.valid_otp) {
        saveAuthFromResponse(data);
        if (arrived !== "login") {
          toast.success("OTP verification succeeded");
        }

        // 1) tell parent OTP is verified (pass true)
        handleOTPVerify?.(true);

        // 2) ask parent to close the dialog (keeps parent and child in sync)
        handleClose?.();

        // 3) if this path needs to call handleNext (user registration), do it AFTER notifying parent
        if (arrived === "userregistration") handleNext?.();
      } else {
        toast(data.message || "Invalid OTP");
      }
    } catch (error) {
      toast.error("OTP verification failed");
      console.error("Error verifying OTP:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={isDialogDisplay}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          handleClose();
        }
      }}
    >
      <DialogTitle  
          sx={{
            background: "linear-gradient(90deg, #f5ffe5ff 0%, #E5FBFF 100%)",
            borderBottom: "1px solid #e0e0e0",
          }}>
         <Stack sx={align_row}>
          <Stack sx={{ ...align_center, width: "100%" }}>
            <MUITypography variant="h3"
            >OTP Verification</MUITypography>
          </Stack>
          <Stack sx={align_right}>
            <IconButton aria-label="close" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 4,
            width: "100%",
            pb: 5,
          }}
        >
          <Box textAlign="center" mt={5}>
            <MUITypography variant="body2" color="text.secondary" label={'Kindly enter the OTP sent to your registered <b>Email/Phone</b>.'}>
              
            </MUITypography>
          </Box>

          {/* OTP Inputs */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            {otp.map((digit, index) => (
              <TextField
                key={index}
                inputRef={(el) => (inputRefs.current[index] = el)}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                inputProps={{
                  maxLength: 1,
                  style: {
                    textAlign: "center",
                    fontSize: "20px",
                    padding: 0,
                  },
                }}
                sx={{
                  width: 48,
                  height: 48,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    borderColor: "#9F9F9F",
                    padding: 0,
                  },
                }}
              />
            ))}
          </Box>

          {/* Resend */}
          <Box
            display="flex"
            gap={1}
            sx={{
              width: "100%",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography fontSize={16} fontWeight={500}>
              Didn’t get OTP?
            </Typography>
            <Button
              onClick={handleResendClick}
              disabled={resendDisabled}
              sx={{
                p: 0,
                m: 0,
                fontSize: 16,
                fontWeight: 500,
                color: "#1E3A8A",
                textTransform: "none",
                backgroundColor: "transparent",
                "&:hover": {
                  textDecoration: "underline",
                  backgroundColor: "transparent",
                },
              }}
            >
              {resendDisabled ? `Resend in ${timer}s` : "Resend Code"}
            </Button>
          </Box>

          {/* Submit */}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleSubmit}
            disabled={otp.join("").length !== 6}
            sx={{
              height: 44,
              borderRadius: "4px",
              fontSize: "16px",
              fontWeight: 500,
              maxWidth: 300,
              mt: 2,
            }}
          >
            Verify OTP
          </Button>
        </Box>
      </DialogContent>

      {loading && <Loader loading={loading} />}
    </Dialog>
  );
};

export default OTPDialog;
